import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Modal,
  Form,
  Input,
  Card,
  Switch,
  Row,
  Col,
  message,
  Space,
  DatePicker,
  Popconfirm,
  Tag,
} from "antd";
import {
  Power,
  PowerOff,
  AlertCircle,
  Clock,
  Edit,
  Trash2,
  Calendar,
} from "lucide-react";
import moment from "moment";
import axios from "axios";
import apiPath from "../../constants/apiPath";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const WebsiteDowntime = () => {
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [isStartModalVisible, setIsStartModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [startForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Get auth token
  const getAuthToken = () => {
    try {
      const loginResponseStr = localStorage.getItem("loginResponse");
      if (loginResponseStr) {
        const loginResponse = JSON.parse(loginResponseStr);
        if (loginResponse.tokens && loginResponse.tokens.access) {
          return loginResponse.tokens.access;
        }
      }
      return localStorage.getItem("token") || localStorage.getItem("access_token");
    } catch (error) {
      console.error("Error getting token:", error);
      return null;
    }
  };

  // API request helper
  const apiRequest = async (endpoint, method, data = null) => {
    const token = getAuthToken();
    const url = `${apiPath.baseURL}${endpoint}`;
    
    const config = {
      method,
      url,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    
    // Only add data for methods that support body (POST, PUT, PATCH)
    if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      config.data = data;
    }
    
    try {
      const response = await axios(config);
      return response.data;
    } catch (error) {
      console.error(`API Error (${url}):`, error);
      throw error;
    }
  };

  // Check if website is currently under maintenance (using local time comparison)
  const isCurrentlyUnderMaintenance = () => {
    if (!currentStatus) return false;
    
    // Only check if status is false (maintenance mode)
    if (currentStatus.status === false && currentStatus.start_time) {
      const now = moment();
      const startTime = moment(currentStatus.start_time);
      const endTime = currentStatus.end_time ? moment(currentStatus.end_time) : null;
      
      if (endTime) {
        return now.isBetween(startTime, endTime);
      } else {
        return now.isAfter(startTime);
      }
    }
    
    return false;
  };

  // Check if maintenance is scheduled for future (using local time comparison)
  const isMaintenanceScheduled = () => {
    if (!currentStatus) return false;
    
    // Only check if status is false (maintenance mode)
    if (currentStatus.status === false && currentStatus.start_time) {
      const now = moment();
      const startTime = moment(currentStatus.start_time);
      return now.isBefore(startTime);
    }
    
    return false;
  };

  // Check if there's any active or scheduled maintenance
  const hasMaintenanceSchedule = () => {
    if (!currentStatus) return false;
    
    // Show only if status is false (maintenance mode) AND has start_time
    return currentStatus.status === false && currentStatus.start_time;
  };

  // Fetch current website status (only called on mount and after actions)
  const fetchCurrentStatus = async () => {
    try {
      const response = await apiRequest(
        apiPath.getWebsiteStatus,
        "GET"
      );
      console.log("Current status response:", response);
      
      if (response?.success && response?.data) {
        setCurrentStatus(response.data);
      } else if (response?.data) {
        setCurrentStatus(response.data);
      } else if (response) {
        setCurrentStatus(response);
      }
    } catch (error) {
      console.error("Error fetching current status:", error);
    }
  };

  // Only fetch on component mount, no interval
  useEffect(() => {
    fetchCurrentStatus();
  }, []);

  // Schedule maintenance
  const handleScheduleMaintenance = async (values) => {
    setLoading(true);
    try {
      const requestData = {
        status: false,
        start_time: values.date_range[0].toISOString(),
        end_time: values.date_range[1].toISOString(),
        reason: values.reason,
      };
      
      const response = await apiRequest(
        apiPath.websiteStatusChange,
        "POST",
        requestData
      );
      
      if (response?.success) {
        await fetchCurrentStatus(); // Refresh after scheduling
        message.success("Maintenance scheduled successfully");
        setIsStartModalVisible(false);
        startForm.resetFields();
      } else {
        message.error(response?.message || "Failed to schedule maintenance");
      }
    } catch (error) {
      console.error("Error scheduling maintenance:", error);
      message.error(error?.response?.data?.message || "Failed to schedule maintenance");
    } finally {
      setLoading(false);
    }
  };

  // Update maintenance
  const handleUpdateMaintenance = async (values) => {
    setLoading(true);
    try {
      const maintenanceId = currentStatus?.id || 1;
      
      const requestData = {
        start_time: values.date_range[0].toISOString(),
        end_time: values.date_range[1].toISOString(),
        reason: values.reason,
      };
      
      const response = await apiRequest(
        apiPath.manageMaintenance(maintenanceId),
        "PUT",
        requestData
      );
      
      if (response?.success) {
        await fetchCurrentStatus(); // Refresh after update
        message.success("Maintenance schedule updated successfully");
        setIsEditModalVisible(false);
        editForm.resetFields();
      } else {
        message.error(response?.message || "Failed to update maintenance");
      }
    } catch (error) {
      console.error("Error updating maintenance:", error);
      message.error(error?.response?.data?.message || "Failed to update maintenance");
    } finally {
      setLoading(false);
    }
  };

  // Cancel maintenance
  const handleCancelMaintenance = async () => {
    setLoading(true);
    try {
      const maintenanceId = currentStatus?.id || 1;
      
      const response = await apiRequest(
        apiPath.manageMaintenance(maintenanceId),
        "DELETE"
      );
      
      if (response?.success) {
        await fetchCurrentStatus(); // Refresh after cancellation
        message.success("Maintenance cancelled successfully");
      } else {
        message.error(response?.message || "Failed to cancel maintenance");
      }
    } catch (error) {
      console.error("Error cancelling maintenance:", error);
      message.error(error?.response?.data?.message || "Failed to cancel maintenance");
    } finally {
      setLoading(false);
    }
  };

  // Turn OFF maintenance immediately
  const handleTurnOffMaintenance = async () => {
    setLoading(true);
    try {
      const requestData = {
        status: true,
      };
      
      const response = await apiRequest(
        apiPath.websiteStatusChange,
        "POST",
        requestData
      );
      
      if (response?.success) {
        await fetchCurrentStatus(); // Refresh after ending maintenance
        message.success("Website is now operational");
      } else {
        message.error(response?.message || "Failed to end maintenance");
      }
    } catch (error) {
      console.error("Error ending maintenance:", error);
      message.error(error?.response?.data?.message || "Failed to end maintenance");
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle change
  const handleToggleChange = async (checked) => {
    if (checked) {
      openStartModal();
    } else {
      if (isCurrentlyUnderMaintenance()) {
        await handleTurnOffMaintenance();
      } else if (isMaintenanceScheduled()) {
        handleCancelMaintenance();
      }
    }
  };

  const openStartModal = () => {
    setIsStartModalVisible(true);
  };

  const openEditModal = () => {
    if (currentStatus) {
      editForm.setFieldsValue({
        date_range: [
          moment(currentStatus.start_time),
          moment(currentStatus.end_time)
        ],
        reason: currentStatus.reason,
      });
      setIsEditModalVisible(true);
    }
  };

  // Calculate duration
  const calculateDuration = (start, end) => {
    if (!start) return "-";
    const duration = moment.duration(moment(end || new Date()).diff(moment(start)));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  // Get current status (recalculates on every render using current time)
  const isDown = isCurrentlyUnderMaintenance();
  const isScheduled = isMaintenanceScheduled();

  const getStatusMessage = () => {
    if (isDown) {
      return "Website is currently under maintenance";
    }
    if (isScheduled && currentStatus?.start_time) {
      return `Maintenance scheduled to start on ${moment(currentStatus.start_time).format("MMM DD, YYYY HH:mm:ss")}`;
    }
    return "Website is running normally";
  };

  const getStatusColor = () => {
    if (isDown) return "#dc2626";
    if (isScheduled) return "#f59e0b";
    return "#22c55e";
  };

  const getStatusTitle = () => {
    if (isDown) return "Under Maintenance";
    if (isScheduled) return "Maintenance Scheduled";
    return "Website Operational";
  };

  const getTagColor = () => {
    if (isDown) return "red";
    if (isScheduled) return "orange";
    return "green";
  };

  // Disable dates in the past
  const disabledDate = (current) => {
    return current && current < moment().startOf('day');
  };

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#f8fafc" }}>
      <Card
        bordered={false}
        style={{
          borderRadius: "16px",
          maxWidth: "700px",
          margin: "0 auto",
          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <Title level={3} style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>
            Website Maintenance Control
          </Title>
          <Text style={{ color: "#64748b", fontSize: "14px" }}>
            Manage website maintenance schedules
          </Text>
        </div>

        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} style={{ textAlign: "center" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "24px",
              }}
            >
              {/* Status Tag */}
              <Tag color={getTagColor()} style={{ fontSize: "14px", padding: "4px 12px" }}>
                {getStatusTitle()}
              </Tag>

              {/* Status Icon */}
              <div
                style={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "60px",
                  background: isDown ? "#fef2f2" : (isScheduled ? "#fffbeb" : "#f0fdf4"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${getStatusColor()}`,
                }}
              >
                {isDown ? (
                  <PowerOff size={60} color={getStatusColor()} />
                ) : (
                  <Power size={60} color={getStatusColor()} />
                )}
              </div>

              {/* Status Text */}
              <div>
                <Title level={2} style={{ margin: 0, color: getStatusColor() }}>
                  {getStatusTitle()}
                </Title>
                <Text style={{ color: "#64748b" }}>
                  {getStatusMessage()}
                </Text>
              </div>

              {/* Action Buttons for Scheduled Maintenance */}
              {isScheduled && !isDown && (
                <Space size="middle">
                  <Button
                    icon={<Edit size={16} />}
                    onClick={openEditModal}
                    type="default"
                  >
                    Update Schedule
                  </Button>
                  <Popconfirm
                    title="Cancel Maintenance"
                    description="Are you sure you want to cancel this scheduled maintenance?"
                    onConfirm={handleCancelMaintenance}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Button icon={<Trash2 size={16} />} danger>
                      Cancel Schedule
                    </Button>
                  </Popconfirm>
                </Space>
              )}

              {/* End Maintenance Button for Active Maintenance */}
              {isDown && (
                <Button
                  onClick={handleTurnOffMaintenance}
                  type="primary"
                  danger
                  loading={loading}
                >
                  End Maintenance Now
                </Button>
              )}

              {/* Toggle Switch */}
              <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "16px" }}>
                <Text strong>Off (Operational)</Text>
                <Switch
                  checked={isDown || isScheduled}
                  onChange={handleToggleChange}
                  loading={loading}
                  size="large"
                  checkedChildren="Maintenance"
                  unCheckedChildren="Operational"
                />
                <Text strong>On (Maintenance)</Text>
              </div>

              {/* Maintenance Info Card - ONLY SHOW WHEN THERE'S ACTIVE OR SCHEDULED MAINTENANCE */}
              {hasMaintenanceSchedule() && (currentStatus?.start_time || currentStatus?.end_time) && (
                <Card
                  size="small"
                  style={{
                    marginTop: "24px",
                    width: "100%",
                    background: isDown ? "#fef2f2" : (isScheduled ? "#fffbeb" : "#f8fafc"),
                    borderColor: isDown ? "#fecaca" : (isScheduled ? "#fde68a" : "#e2e8f0"),
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    {currentStatus.start_time && (
                      <div>
                        <Calendar size={14} style={{ marginRight: "8px" }} />
                        <Text strong>Maintenance Start:</Text>{" "}
                        <Text>{moment(currentStatus.start_time).format("MMM DD, YYYY HH:mm:ss")}</Text>
                      </div>
                    )}
                    {currentStatus.end_time && (
                      <div>
                        <Clock size={14} style={{ marginRight: "8px" }} />
                        <Text strong>Maintenance End:</Text>{" "}
                        <Text>{moment(currentStatus.end_time).format("MMM DD, YYYY HH:mm:ss")}</Text>
                      </div>
                    )}
                    {currentStatus.start_time && currentStatus.end_time && (
                      <div>
                        <Clock size={14} style={{ marginRight: "8px" }} />
                        <Text strong>Total Duration:</Text>{" "}
                        <Text>
                          {calculateDuration(currentStatus.start_time, currentStatus.end_time)}
                        </Text>
                      </div>
                    )}
                    {currentStatus.reason && (
                      <div>
                        <AlertCircle size={14} style={{ marginRight: "8px" }} />
                        <Text strong>Reason:</Text>{" "}
                        <Text>{currentStatus.reason}</Text>
                      </div>
                    )}
                    
                    {isScheduled && currentStatus.start_time && (
                      <div style={{ marginTop: "8px", color: "#f59e0b" }}>
                        <AlertCircle size={14} style={{ marginRight: "8px" }} />
                        <Text strong>Status:</Text>{" "}
                        <Text>Maintenance will begin in {moment(currentStatus.start_time).fromNow()}</Text>
                      </div>
                    )}
                  </Space>
                </Card>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      {/* Schedule Maintenance Modal */}
      <Modal
        title="Schedule Website Maintenance"
        open={isStartModalVisible}
        onCancel={() => {
          setIsStartModalVisible(false);
          startForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
        width={500}
      >
        <Form form={startForm} layout="vertical" onFinish={handleScheduleMaintenance}>
          <Form.Item
            name="date_range"
            label="Maintenance Schedule"
            rules={[{ required: true, message: "Please select maintenance start and end time" }]}
            extra="Select when the maintenance will begin and end"
          >
            <RangePicker
              showTime={{ format: "HH:mm:ss" }}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder={["Start Date & Time", "End Date & Time"]}
              style={{ width: "100%", borderRadius: "8px" }}
              disabledDate={disabledDate}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Maintenance Reason"
            rules={[{ required: true, message: "Please provide maintenance reason" }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="e.g., Scheduled maintenance, Server updates, Security patches, etc."
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
            <Button onClick={() => setIsStartModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Schedule Maintenance
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit Maintenance Modal */}
      <Modal
        title="Update Maintenance Schedule"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
        width={500}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateMaintenance}>
          <Form.Item
            name="date_range"
            label="Maintenance Schedule"
            rules={[{ required: true, message: "Please select maintenance start and end time" }]}
          >
            <RangePicker
              showTime={{ format: "HH:mm:ss" }}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder={["Start Date & Time", "End Date & Time"]}
              style={{ width: "100%", borderRadius: "8px" }}
              disabledDate={disabledDate}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Maintenance Reason"
            rules={[{ required: true, message: "Please provide maintenance reason" }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="e.g., Scheduled maintenance, Server updates, Security patches, etc."
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
            <Button onClick={() => setIsEditModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update Schedule
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default WebsiteDowntime;